/**
 * Parser de Talemark (docs/spec/talemark.md): texto do chat → árvore de blocos.
 * A renderização fica a cargo de quem consome (React no cliente).
 */
import { deskFileRefId } from '../deskCloud.js';
import { resolveTalemarkColor } from './palette.js';

export type TalemarkOptions = {
  charAction: boolean;
  quotedSpeech: boolean;
  emDashSpeech: boolean;
  ooc: boolean;
  initialCaps: boolean;
  /** `# título` vira título (senão fica como texto) */
  headings?: boolean;
  /** `---` sozinho na linha vira linha horizontal */
  horizontalLines?: boolean;
  /** `:)` e `:sorriso:` viram emoji */
  smileys?: boolean;
};

export const DEFAULT_TALEMARK_OPTIONS: TalemarkOptions = {
  charAction: true,
  quotedSpeech: true,
  emDashSpeech: true,
  ooc: true,
  initialCaps: true,
  headings: true,
  horizontalLines: true,
  smileys: true,
};

/** Smileys de texto e atalhos (os nossos), trocados por emoji Unicode. */
const SMILEYS: Record<string, string> = {
  ':)': '🙂',
  ':-)': '🙂',
  ':D': '😄',
  ':-D': '😄',
  ';)': '😉',
  ';-)': '😉',
  ':(': '🙁',
  ':-(': '🙁',
  ':P': '😛',
  ':p': '😛',
  ':O': '😮',
  ':o': '😮',
  "<3": '❤️',
  ':sorriso:': '😄',
  ':risada:': '😂',
  ':piscada:': '😉',
  ':triste:': '😢',
  ':raiva:': '😠',
  ':coracao:': '❤️',
  ':coração:': '❤️',
  ':dado:': '🎲',
  ':espada:': '⚔️',
  ':escudo:': '🛡️',
  ':caveira:': '💀',
  ':fogo:': '🔥',
  ':magia:': '✨',
  ':ok:': '👍',
};
const SMILEY_RE = new RegExp(
  `(^|\\s)(${Object.keys(SMILEYS)
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})(?=$|\\s|[.,!?])`,
  'gu',
);

/** Troca smileys soltos (entre espaços) por emoji, antes de interpretar a marcação. */
export function replaceSmileys(text: string): string {
  return text.replace(SMILEY_RE, (_m, pre: string, key: string) => `${pre}${SMILEYS[key] ?? key}`);
}

/** Estado dos códigos clássicos aplicado a cada trecho de texto. */
export type TMClassicStyle = {
  bold?: true;
  italic?: true;
  underline?: true;
  strike?: true;
  color?: string;
  background?: string;
};

export type TMSpanKind = 'em' | 'strong' | 'action' | 'quote' | 'emdash' | 'ooc';

export type TMInline =
  | { t: 'text'; text: string; style?: TMClassicStyle }
  | { t: 'br' }
  | { t: 'span'; kind: TMSpanKind; children: TMInline[] }
  | { t: 'initialCap'; char: string }
  | { t: 'url'; href: string }
  | { t: 'mention'; name: string }
  | { t: 'image'; src: string };

export type TMBlock =
  | { t: 'p'; children: TMInline[] }
  | { t: 'h'; level: number; children: TMInline[] }
  | { t: 'hr' };

/* ------------------------------------------------------------------ blocos */

/**
 * Os arquivos do Desk Cloud das imagens `[§I rpgdesk-file:<id>]` do texto, na ordem e sem repetir:
 * o servidor confere cada um antes de a mensagem ir para o chat.
 */
export function talemarkFileImages(text: string): string[] {
  const ids: string[] = [];
  for (const m of text.matchAll(/\[§i([^\]]*)\]/gi)) {
    const id = deskFileRefId(m[1]!.trim());
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

export function parseTalemark(text: string, options: TalemarkOptions = DEFAULT_TALEMARK_OPTIONS): TMBlock[] {
  const blocks: TMBlock[] = [];
  const source = options.smileys === false ? text : replaceSmileys(text);
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  let para: string[] = [];
  let first = true;
  const inline = (src: string, cap: boolean) => parseInline(src, options, cap);
  const flush = () => {
    if (para.length) {
      blocks.push({ t: 'p', children: inline(para.join('\n'), first) });
      first = false;
    }
    para = [];
  };
  for (const line of lines) {
    const heading = options.headings === false ? null : /^(#{1,6})\s+(.*)$/.exec(line);
    if (/^\s*$/.test(line)) {
      flush();
    } else if (options.horizontalLines !== false && /^\s*(?:(?:-\s*){3,}|(?:\*\s*){3,}|(?:_\s*){3,})$/.test(line) && !para.length) {
      blocks.push({ t: 'hr' });
    } else if (heading) {
      flush();
      blocks.push({ t: 'h', level: heading[1]!.length, children: inline(heading[2]!, false) });
      first = false;
    } else {
      para.push(line);
    }
  }
  flush();
  return blocks;
}

/* ------------------------------------------------------------------ inline: tokens */

type Delim = {
  t: 'delim';
  char: '*' | '_' | '"' | '-' | '(' | ')';
  len: number;
  canOpen: boolean;
  canClose: boolean;
  style?: TMClassicStyle;
};
type Tok = TMInline | Delim;

const URL_RE = /^https?:\/\/[^\s<>"'【】]+[^\s<>"'.,;:!?)\]】]/i;
const MENTION_RE = /^@([\p{L}\p{N}_.-]{2,24})/u;
const CLASSIC_RE = /^\[§([a-z])([^\]]*)\]/i;

const isSpace = (c: string | undefined) => c === undefined || /\s/u.test(c);
const isPunct = (c: string | undefined) => c !== undefined && /[\p{P}\p{S}]/u.test(c);

function leftFlanking(before: string | undefined, after: string | undefined): boolean {
  if (isSpace(after)) return false;
  return !isPunct(after) || isSpace(before) || isPunct(before);
}
function rightFlanking(before: string | undefined, after: string | undefined): boolean {
  if (isSpace(before)) return false;
  return !isPunct(before) || isSpace(after) || isPunct(after);
}

function tokenize(src: string, options: TalemarkOptions, allowInitialCap: boolean): Tok[] {
  const out: Tok[] = [];
  let style: TMClassicStyle = {};
  let buf = '';
  const pushText = () => {
    if (!buf) return;
    out.push(Object.keys(style).length ? { t: 'text', text: buf, style: { ...style } } : { t: 'text', text: buf });
    buf = '';
  };

  let i = 0;
  if (allowInitialCap && options.initialCaps) {
    const m = /^\{(\S)\}/u.exec(src);
    if (m) {
      out.push({ t: 'initialCap', char: m[1]! });
      i = m[0].length;
    }
  }

  while (i < src.length) {
    const rest = src.slice(i);
    const c = src[i]!;
    const prev = i > 0 ? src[i - 1] : undefined;

    const classic = CLASSIC_RE.exec(rest);
    if (classic) {
      pushText();
      const code = classic[1]!.toUpperCase();
      const param = classic[2]!.trim();
      if (code === 'B') style = toggle(style, 'bold');
      else if (code === 'T') style = toggle(style, 'italic');
      else if (code === 'U') style = toggle(style, 'underline');
      else if (code === 'S') style = toggle(style, 'strike');
      else if (code === 'O') style = {};
      else if (code === 'R') {
        const { color, background, ...restStyle } = style;
        style = { ...restStyle, ...(background ? { color: background } : { color: 'var(--bkg-color)' }), ...(color ? { background: color } : { background: 'var(--face-color)' }) };
      } else if (code === 'K') {
        const [fg, bg] = param.split(',');
        const fgc = fg !== undefined ? resolveTalemarkColor(fg) : null;
        const bgc = bg !== undefined ? resolveTalemarkColor(bg) : null;
        if (fgc) style = { ...style, color: fgc };
        if (bgc) style = { ...style, background: bgc };
        if (!fgc && !bgc) {
          const { color: _c, background: _b, ...restStyle } = style;
          style = restStyle;
        }
      } else if (code === 'I' && (/^https?:\/\//i.test(param) || deskFileRefId(param))) {
        // `rpgdesk-file:<id>`: arquivo do Desk Cloud, cujo link quem desenha pede na hora
        out.push({ t: 'image', src: param });
      } else {
        buf += classic[0]; // código desconhecido: fica como texto
        i += classic[0].length;
        continue;
      }
      i += classic[0].length;
      continue;
    }

    if (c === '\n') {
      pushText();
      out.push({ t: 'br' });
      i++;
      continue;
    }

    if ((c === 'h' || c === 'H') && (isSpace(prev) || prev === '(')) {
      const url = URL_RE.exec(rest);
      if (url) {
        pushText();
        out.push({ t: 'url', href: url[0] });
        i += url[0].length;
        continue;
      }
    }

    if (c === '@' && (isSpace(prev) || isPunct(prev))) {
      const m = MENTION_RE.exec(rest);
      if (m) {
        pushText();
        out.push({ t: 'mention', name: m[1]! });
        i += m[0].length;
        continue;
      }
    }

    if (c === '*' || c === '_' || c === '"' || c === '-' || c === '(' || c === ')') {
      let len = 1;
      while (src[i + len] === c && len < 8) len++;
      const before = prev;
      const after = src[i + len];
      const lf = leftFlanking(before, after);
      const rf = rightFlanking(before, after);
      let delim: Delim | null = null;
      if ((c === '*' || c === '_') && len <= 2) {
        // "_" dentro de palavra não abre/fecha (como no CommonMark)
        const canOpen = c === '_' ? lf && (!rf || isPunct(before)) : lf;
        const canClose = c === '_' ? rf && (!lf || isPunct(after)) : rf;
        delim = { t: 'delim', char: c, len, canOpen, canClose };
      } else if (c === '"' && len === 1 && options.quotedSpeech) {
        delim = { t: 'delim', char: c, len, canOpen: lf, canClose: rf };
      } else if (c === '-' && len === 2 && options.emDashSpeech) {
        delim = { t: 'delim', char: c, len, canOpen: isSpace(before) || before === undefined || lf, canClose: rf || isSpace(after) };
      } else if ((c === '(' || c === ')') && len === 2 && options.ooc) {
        delim = { t: 'delim', char: c, len, canOpen: c === '(', canClose: c === ')' };
      }
      if (delim) {
        pushText();
        if (Object.keys(style).length) delim.style = { ...style };
        out.push(delim);
        i += len;
        continue;
      }
      buf += src.slice(i, i + len);
      i += len;
      continue;
    }

    buf += c;
    i++;
  }
  pushText();
  return out;
}

function toggle(style: TMClassicStyle, key: 'bold' | 'italic' | 'underline' | 'strike'): TMClassicStyle {
  const next = { ...style };
  if (next[key]) delete next[key];
  else next[key] = true;
  return next;
}

/* ------------------------------------------------------------------ inline: casamento */

function kindOf(d: Delim, options: TalemarkOptions): TMSpanKind {
  switch (d.char) {
    case '*':
      return d.len === 2 ? 'strong' : options.charAction ? 'action' : 'em';
    case '_':
      return d.len === 2 ? 'strong' : 'em';
    case '"':
      return 'quote';
    case '-':
      return 'emdash';
    default:
      return 'ooc';
  }
}

function matches(opener: Delim, closer: Delim): boolean {
  if (opener.char === '(') return closer.char === ')';
  return opener.char === closer.char && opener.len === closer.len;
}

function delimText(d: Delim): TMInline {
  const text = d.char.repeat(d.len);
  return d.style ? { t: 'text', text, style: d.style } : { t: 'text', text };
}

export function parseInline(src: string, options: TalemarkOptions = DEFAULT_TALEMARK_OPTIONS, allowInitialCap = false): TMInline[] {
  const toks = tokenize(src, options, allowInitialCap);
  // pilha de "quadros": cada abridor pendente guarda o que veio depois dele
  type Frame = { opener: Delim | null; items: TMInline[] };
  const stack: Frame[] = [{ opener: null, items: [] }];
  const top = () => stack[stack.length - 1]!;

  const closeAt = (idx: number, closer: Delim | null) => {
    // fecha o quadro idx; quadros acima dele que não fecharam viram texto
    while (stack.length - 1 > idx) {
      const f = stack.pop()!;
      top().items.push(delimText(f.opener!), ...f.items);
    }
    const f = stack.pop()!;
    top().items.push({ t: 'span', kind: kindOf(f.opener!, options), children: f.items });
    void closer;
  };

  for (const tok of toks) {
    if (tok.t !== 'delim') {
      if (tok.t === 'br') {
        // fala com travessão sem fechamento termina no fim da linha
        const idx = stack.findIndex((f) => f.opener?.char === '-');
        if (idx > 0) closeAt(idx, null);
      }
      top().items.push(tok);
      continue;
    }
    if (tok.canClose) {
      let idx = -1;
      for (let s = stack.length - 1; s > 0; s--) {
        if (matches(stack[s]!.opener!, tok)) {
          idx = s;
          break;
        }
      }
      if (idx > 0) {
        closeAt(idx, tok);
        continue;
      }
    }
    if (tok.canOpen) {
      stack.push({ opener: tok, items: [] });
      continue;
    }
    top().items.push(delimText(tok));
  }
  // fim do texto: travessão aberto fecha; o resto vira texto
  while (stack.length > 1) {
    const f = top();
    if (f.opener!.char === '-') closeAt(stack.length - 1, null);
    else {
      stack.pop();
      top().items.push(delimText(f.opener!), ...f.items);
    }
  }
  return mergeText(stack[0]!.items);
}

function mergeText(items: TMInline[]): TMInline[] {
  const out: TMInline[] = [];
  for (const it of items) {
    const last = out[out.length - 1];
    if (it.t === 'text' && last?.t === 'text' && JSON.stringify(last.style) === JSON.stringify(it.style)) {
      out[out.length - 1] = { ...last, text: last.text + it.text };
    } else if (it.t === 'span') {
      out.push({ ...it, children: mergeText(it.children) });
    } else {
      out.push(it);
    }
  }
  return out;
}

/** Texto puro (sem marcação), para notificações e buscas. */
export function talemarkToPlainText(blocks: TMBlock[]): string {
  const inl = (xs: TMInline[]): string =>
    xs
      .map((x) => {
        switch (x.t) {
          case 'text':
            return x.text;
          case 'br':
            return '\n';
          case 'initialCap':
            return x.char;
          case 'url':
            return x.href;
          case 'mention':
            return `@${x.name}`;
          case 'image':
            return '';
          case 'span':
            return inl(x.children);
        }
      })
      .join('');
  return blocks.map((b) => (b.t === 'hr' ? '———' : inl(b.children))).join('\n');
}
